import React from 'react';
import { BrandAsset } from '../types';
import { Trash2, ArrowLeft, Undo2 } from 'lucide-react';
import { Button } from '../components/UI';

interface MediaLibraryProps {
  assets: BrandAsset[];
  onDelete: (id: string) => void;
  onRestore?: (id: string) => void; // Optional restore
  onBack: () => void;
}

export const MediaLibrary: React.FC<MediaLibraryProps> = ({ assets, onDelete, onRestore, onBack }) => {
  // We show all assets here, grouping by trash or not
  const deletedAssets = assets.filter(a => !!a.deletedAt);
  const activeAssets = assets.filter(a => !a.deletedAt);

  return (
    <div className="container mx-auto p-8">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={onBack}><ArrowLeft/></Button>
        <h1 className="text-3xl font-bold">Media Library</h1>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Trash2 className="text-red-500"/> Trash Bin ({deletedAssets.length})</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
          {deletedAssets.map(asset => (
            <div key={asset.id} className="relative group bg-red-50 rounded-xl shadow-sm border border-red-100 p-4 aspect-square flex items-center justify-center grayscale opacity-75 hover:grayscale-0 hover:opacity-100 transition-all">
              <img src={asset.url} className="max-w-full max-h-full object-contain" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl gap-2">
                <button 
                  onClick={() => onDelete(asset.id)} // Permanent Delete
                  className="bg-white text-red-600 p-3 rounded-full hover:bg-red-50"
                  title="Permanently Delete"
                >
                  <Trash2 size={20} />
                </button>
                {onRestore && (
                   <button 
                    onClick={() => onRestore(asset.id)} 
                    className="bg-white text-green-600 p-3 rounded-full hover:bg-green-50"
                    title="Restore"
                  >
                    <Undo2 size={20} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {deletedAssets.length === 0 && <p className="text-gray-400 text-sm">Trash is empty.</p>}
        </div>
      </div>

      <div>
         <h2 className="text-xl font-bold mb-4">Active Assets ({activeAssets.length})</h2>
         <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
          {activeAssets.map(asset => (
            <div key={asset.id} className="relative group bg-white rounded-xl shadow-sm border p-4 aspect-square flex items-center justify-center">
              <img src={asset.url} className="max-w-full max-h-full object-contain" />
              <div className="absolute bottom-2 left-2 text-[10px] bg-black/70 text-white px-2 py-0.5 rounded">
                {asset.type}
              </div>
            </div>
          ))}
         </div>
      </div>
    </div>
  );
};